import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Trash2, Phone, Mail } from "lucide-react";

type Lead = {
  id: string; name: string; mobile: string; email: string | null;
  service_type: string; message: string | null; status: string; created_at: string;
};

export function LeadsTab() {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    const { data, error } = await supabase.from("leads").select("*").order("created_at", { ascending: false });
    if (error) toast.error(error.message); else setLeads(data || []);
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  async function setStatus(id: string, status: string) {
    const { error } = await supabase.from("leads").update({ status }).eq("id", id);
    if (error) toast.error(error.message); else { toast.success("Updated"); load(); }
  }
  async function remove(id: string) {
    if (!confirm("Delete this lead?")) return;
    const { error } = await supabase.from("leads").delete().eq("id", id);
    if (error) toast.error(error.message); else { toast.success("Deleted"); load(); }
  }

  const pending = leads.filter((l) => l.status === "pending").length;

  if (loading) return <p className="text-muted-foreground">Loading...</p>;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl">Booking Requests</h2>
          <p className="text-sm text-muted-foreground">{leads.length} total · {pending} pending</p>
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-border bg-card">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left">
            <tr>
              <th className="p-3">Date</th>
              <th className="p-3">Customer</th>
              <th className="p-3">Service</th>
              <th className="p-3">Status</th>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {leads.length === 0 && (
              <tr><td colSpan={5} className="p-6 text-center text-muted-foreground">No leads yet.</td></tr>
            )}
            {leads.map((l) => (
              <tr key={l.id} className="border-t border-border">
                <td className="p-3 whitespace-nowrap text-muted-foreground">{new Date(l.created_at).toLocaleDateString()}</td>
                <td className="p-3">
                  <div className="font-medium">{l.name}</div>
                  <div className="text-xs text-muted-foreground flex flex-wrap gap-2">
                    <a href={`tel:${l.mobile}`} className="hover:text-primary inline-flex items-center gap-1"><Phone className="h-3 w-3" />{l.mobile}</a>
                    {l.email && <a href={`mailto:${l.email}`} className="hover:text-primary inline-flex items-center gap-1"><Mail className="h-3 w-3" />{l.email}</a>}
                  </div>
                  {l.message && <div className="mt-1 text-xs text-muted-foreground italic">"{l.message}"</div>}
                </td>
                <td className="p-3">{l.service_type}</td>
                <td className="p-3">
                  <Badge variant={l.status === "completed" ? "default" : "secondary"} className={l.status === "completed" ? "bg-success text-success-foreground" : ""}>
                    {l.status}
                  </Badge>
                </td>
                <td className="p-3 text-right whitespace-nowrap">
                  {l.status === "pending" ? (
                    <Button size="sm" onClick={() => setStatus(l.id, "completed")}>Mark Done</Button>
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => setStatus(l.id, "pending")}>Reopen</Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => remove(l.id)} className="ml-1 text-destructive">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
